import React, { useState, useEffect } from "react";
import { adminApi } from "../../api";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  MousePointer,
  TrendingUp,
  Calendar,
  ArrowUpRight,
} from "lucide-react";

const COLORS = ["#007AFF", "#10B981", "#F59E0B", "#8B5CF6", "#EC4899", "#06B6D4"];

const AnalyticsPage = () => {
  const [analytics, setAnalytics] = useState(null);
  const [niches, setNiches] = useState([]);
  const [selectedNiche, setSelectedNiche] = useState("all");
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [analyticsRes, nichesRes] = await Promise.all([
        adminApi.getAnalytics(selectedNiche === "all" ? null : selectedNiche),
        adminApi.getNiches(),
      ]);
      setAnalytics(analyticsRes.data);
      setNiches(nichesRes.data);
    } catch (error) {
      console.error("Failed to load analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedNiche]);

  const sourceData = analytics?.clicks_by_source
    ? Object.entries(analytics.clicks_by_source).map(([name, value]) => ({
        name,
        value,
      }))
    : [];

  const topProductsData = analytics?.top_products?.slice(0, 10) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="analytics-loading">
        <div className="animate-pulse text-[#A1A1AA]">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Analytics</h1>
          <p className="text-[#A1A1AA] text-sm mt-1">
            Track affiliate link performance
          </p>
        </div>
        <select
          value={selectedNiche}
          onChange={(e) => setSelectedNiche(e.target.value)}
          className="bg-[#1A1A1A] border border-white/10 text-white rounded-md px-3 py-2 text-sm"
          data-testid="analytics-niche-filter"
        >
          <option value="all">All Niches</option>
          {niches.map((niche) => (
            <option key={niche.id} value={niche.id}>
              {niche.name}
            </option>
          ))}
        </select>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-[#121212] border border-white/10 rounded-lg p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#A1A1AA] text-sm font-mono">TOTAL CLICKS</p>
              <p className="text-3xl font-bold text-white mt-2">
                {analytics?.total_clicks || 0}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-[#007AFF]/20">
              <MousePointer size={24} className="text-[#007AFF]" />
            </div>
          </div>
        </div>

        <div className="bg-[#121212] border border-white/10 rounded-lg p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#A1A1AA] text-sm font-mono">TODAY</p>
              <p className="text-3xl font-bold text-white mt-2">
                {analytics?.clicks_today || 0}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-emerald-500/20">
              <TrendingUp size={24} className="text-emerald-400" />
            </div>
          </div>
        </div>

        <div className="bg-[#121212] border border-white/10 rounded-lg p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#A1A1AA] text-sm font-mono">THIS WEEK</p>
              <p className="text-3xl font-bold text-white mt-2">
                {analytics?.clicks_this_week || 0}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-[#F59E0B]/20">
              <Calendar size={24} className="text-[#F59E0B]" />
            </div>
          </div>
        </div>

        <div className="bg-[#121212] border border-white/10 rounded-lg p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#A1A1AA] text-sm font-mono">THIS MONTH</p>
              <p className="text-3xl font-bold text-white mt-2">
                {analytics?.clicks_this_month || 0}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-[#8B5CF6]/20">
              <ArrowUpRight size={24} className="text-[#8B5CF6]" />
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Products */}
        <div className="bg-[#121212] border border-white/10 rounded-lg p-5">
          <h3 className="text-white font-semibold mb-4">Top Products by Clicks</h3>
          {topProductsData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topProductsData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#2A2A2A" />
                <XAxis type="number" stroke="#A1A1AA" fontSize={12} />
                <YAxis
                  type="category"
                  dataKey="product_name"
                  stroke="#A1A1AA"
                  fontSize={12}
                  width={120}
                  tick={{ fill: "#A1A1AA" }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1A1A1A",
                    border: "1px solid #2A2A2A",
                    borderRadius: "8px",
                  }}
                />
                <Bar dataKey="clicks" fill="#007AFF" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-[#A1A1AA]">
              No click data yet
            </div>
          )}
        </div>

        {/* Clicks by Source */}
        <div className="bg-[#121212] border border-white/10 rounded-lg p-5">
          <h3 className="text-white font-semibold mb-4">Clicks by Source</h3>
          {sourceData.length > 0 ? (
            <div className="flex items-center">
              <ResponsiveContainer width="50%" height={250}>
                <PieChart>
                  <Pie
                    data={sourceData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {sourceData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1A1A1A",
                      border: "1px solid #2A2A2A",
                      borderRadius: "8px",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="w-1/2 space-y-2">
                {sourceData.map((entry, index) => (
                  <div key={entry.name} className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <span className="text-[#A1A1AA] text-sm flex-1">
                      {entry.name}
                    </span>
                    <span className="text-white font-mono text-sm">
                      {entry.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-[250px] flex items-center justify-center text-[#A1A1AA]">
              No source data yet
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
